import { createContext, useContext, useMemo } from "react";
import type { Locale } from "@salesboard/contracts";
import { getActiveLocale } from "./active-locale";
import { cs } from "./cs";
import { en } from "./en";

/**
 * The message catalogue — typed, in-repo, no library.
 *
 * The whole design is one idea: **`en.ts` defines the vocabulary and `cs.ts` is typed against
 * it**, so a missing translation is a compile error rather than an English word appearing in a
 * Czech screen. That is the same mechanism the audit registry uses to make an undescribed event
 * type impossible, and it is why two locales and one product do not need a runtime i18n
 * framework: there is nothing to look up at runtime that the compiler has not already checked.
 *
 * Usage:
 *
 * ```tsx
 * const t = useT();
 * t("nav.pipeline");                     // → "Pipeline"
 * t("board.emptyTitle", 2026);           // arguments are typed per key
 * t("count.opportunities", 5);           // → "5 příležitostí"
 * ```
 *
 * There is deliberately **no `t(key, ...)` escape hatch for a dynamic string**: every key is a
 * literal in the source, which is what makes "find every place this sentence appears" a
 * grep and "is every key translated" a typecheck.
 */
export type Catalogue = typeof en;

export type MessageKey = keyof Catalogue;

/** What a catalogue entry may be: a fixed string, or a typed template. */
export type Message = string | ((...args: never[]) => string);

/** The vocabulary as a flat record, which is what `cs.ts` has to satisfy. */
export type Messages = Record<MessageKey, Message>;

const CATALOGUES: Record<Locale, Catalogue> = { cs, en };

/** The arguments a given key takes — none for a plain string. */
type MessageArgs<K extends MessageKey> = Catalogue[K] extends (...args: infer A) => string ? A : [];

export type Translator = <K extends MessageKey>(key: K, ...args: MessageArgs<K>) => string;

/** A translator for one locale. Pure, so tests can assert a language without rendering. */
export function translator(locale: Locale): Translator {
  const catalogue = CATALOGUES[locale];
  return (key, ...args) => {
    const value: Message = catalogue[key];
    if (typeof value === "string") return value;
    // The cast is confined to this line: `MessageArgs<K>` has already proved at every call
    // site that the arguments match this key's signature.
    return (value as (...a: unknown[]) => string)(...(args as unknown[]));
  };
}

/**
 * The locale the tree is rendering in.
 *
 * Published by `LocaleProvider` (in `app/`), which is the only place the session is read — so
 * `i18n/` stays a leaf that knows about languages and nothing about authentication.
 *
 * Its default is `undefined` rather than a language, and `useLocale` falls back to the
 * **active** locale instead. That keeps one source of truth: the provider sets both, so a
 * component rendered outside it — a unit test mounting one screen, a component in isolation —
 * reads the same answer the formatters do rather than a second default that could disagree
 * with them.
 */
export const LocaleContext = createContext<Locale | undefined>(undefined);

export function useLocale(): Locale {
  return useContext(LocaleContext) ?? getActiveLocale();
}

export function useT(): Translator {
  const locale = useLocale();
  return useMemo(() => translator(locale), [locale]);
}

export {
  getActiveLocale,
  resolveLocale,
  setActiveLocale,
  browserLanguages,
  readStoredLocale,
  writeStoredLocale,
} from "./active-locale";
export { pluralForm, pluralFor, type PluralForms } from "./plural";
export { apiErrorMessage, ERROR_MESSAGE_KEYS } from "./errorMessage";
